import { useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Box, IconButton } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  // setStatus,
} from '../../../store/orderUserWise/orderUserWiseSlice';
import Loader from '../../../components/Loader';
import Scrollbar from '../../../components/Scrollbar';
import { setStatus as setWalletTransactionStatus } from '../../../store/walletTransaction/walletTransactionSlice';
import { Users } from '../../../store/users/api/interfaces';
import { findOrderUserWise } from '../../../store/orderUserWise/api';
import { fDateTime } from '../../../utils/formatTime';
import { selectPaymentStatusColor } from '../../../utils/selectPaymentStatusColor';
import Label from '../../../components/Label';
import { encryptString } from '../../../utils/url';
import { Link } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

interface IProps {
  user: Users | undefined;
}

const OrderHistoryTab: React.FC<IProps> = ({ user }) => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.orderUserWise);

  useEffect(() => {
    if (user?.id) {
      dispatch(
        findOrderUserWise({
          limit,
          skip,
          userId: user.id,
        })
      );
    }
  }, [dispatch, limit, skip, user]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setWalletTransactionStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  return (
    <>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 1000, position: 'relative', pt: 3 }}>
          <Table size={'medium'}>
            <TableHead>
              <TableRow>
                <TableCell align='center'>{'Order No.'}</TableCell>
                <TableCell align='center'>{'User'}</TableCell>
                <TableCell align='center'>{'Order Date'}</TableCell>
                <TableCell align='center'>{'Total Amount'}</TableCell>
                <TableCell align='center'>{'Status'}</TableCell>
                <TableCell align='center'>{'Action'}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow hover>
                  <TableCell
                    colSpan={7}
                    align='center'
                  >
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : data?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align='center'
                  >
                    {'No record found'}
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((orderUserWise) => (
                  <TableRow key={orderUserWise.id}>
                    <TableCell align='center'>
                      {orderUserWise?.orderNumber}
                    </TableCell>
                    <TableCell align='center'>
                      {orderUserWise?.userDetails?.firstName}{' '}
                      {orderUserWise?.userDetails?.lastName}
                    </TableCell>
                    <TableCell align='center'>
                      {fDateTime(orderUserWise?.orderDate)}
                    </TableCell>
                    <TableCell align='center'>
                      {/* {orderUserWise?.totalAmount} */}
                      {parseFloat(
                        Number(orderUserWise?.totalAmount ?? 0).toFixed(2)
                      )}
                    </TableCell>
                    <TableCell align='center'>
                      <Label
                        variant='ghost'
                        color={selectPaymentStatusColor(orderUserWise?.status)}
                      >
                        {orderUserWise?.status}
                      </Label>
                    </TableCell>
                    <TableCell align='center'>
                      <IconButton aria-label='visibility'>
                        <Link
                          to={`/order/view/${encryptString(orderUserWise?.id ?? '')}`}
                          style={{ color: '#808080' }}
                        >
                          <RemoveRedEyeIcon />
                        </Link>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component='div'
          count={totalRecords}
          rowsPerPage={limit}
          page={Math.min(Math.round(totalRecords / limit), currentPage)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </>
  );
};

export default OrderHistoryTab;
